import {
  Box,
  Card as MuiCard,
  Chip,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import Card from '@/components/wrapped/Card';
import MobileKeyboard from '@/designs-dev/device/MobileKeyboard';
import Screen from '@/karehero-app/explore/Screen';
import Chat, { ChatInput, TalkInput } from '@/karehero-app/explore/Chat';
import { FLOW } from '@/karehero-app/explore/tell-us-once/TellUsOnce.content';

// The high-level flow: the whole journey in one strip, at the lowest fidelity
// that still makes the story legible. The lettered sections each interrogate a
// single moment; this is the thing they sit inside. Screens that already exist
// elsewhere in the exploration are reused rather than redrawn, so the flow
// can't drift from the frames that argue about it.

// F2 — home, with one task. One rather than a list, because at this point
// there is exactly one thing worth doing and a list of one reads as a stub.
export const F2Home = () => {
  const c = FLOW.home;
  return (
    <Screen footer={<TalkInput typePrompt={c.typePrompt} state='idle' />}>
      <Stack sx={{ pt: 2 }} spacing={3}>
        <Stack spacing={0.5}>
          <Typography variant='body2' color='text.secondary'>
            {c.greeting}
          </Typography>
          <Typography variant='h2'>{c.lead}</Typography>
        </Stack>

        <Card
          title={c.task.title}
          variant='compact'
          subtitle={c.task.detail}
          chip={<Chip size='small' label={c.task.chip} color='warmOrange' />}
          onClick={() => {}}
        />
      </Stack>
    </Screen>
  );
};
F2Home.displayName = 'F2Home';

// F3 — establishing who is asking. The note under the transcript carries the
// branch: a carer names two people, someone claiming for themselves names one.
// Asking a self-claimant for "their" name too is exactly the clumsiness this
// whole idea exists to remove.
export const F3WhoAreYou = () => {
  const c = FLOW.whoAreYou;
  return (
    <Screen
      footer={
        <Button ariaLabel={c.cta} isFullWidth>
          {c.cta}
        </Button>
      }
    >
      <Stack sx={{ pt: 1 }} spacing={2}>
        <Chat turns={c.turns} />

        {/* Names are typed, not spoken. A misheard name on a government claim
            is a rejection, so this is the one place the conversation hands
            over to fields — and it says why rather than just doing it. */}
        <Stack sx={{ pt: 1 }} spacing={2}>
          {c.fields.map((f) => (
            <TextField
              key={f.label}
              label={f.label}
              value={f.value}
              size='small'
              fullWidth
            />
          ))}
        </Stack>

        <Stack
          sx={{ alignItems: 'flex-start', pl: 1 }}
          direction='row'
          spacing={1.5}
        >
          <Box sx={{ color: 'custom.cherry.500', display: 'flex', pt: '2px' }}>
            <Icon icon={'chevrons-down' as never} fontSize={12} />
          </Box>
          <Typography variant='caption' color='text.secondary'>
            {c.branchNote}
          </Typography>
        </Stack>
      </Stack>
    </Screen>
  );
};
F3WhoAreYou.displayName = 'F3WhoAreYou';

// F5 — the handoff from talking to typing, caught mid-type with the keyboard
// up. The question the frame is really asking is how abrupt that switch feels
// when everything either side of it is spoken.
export const F5Typing = () => {
  const c = FLOW.typing;
  return (
    <Screen>
      <Stack sx={{ height: '100%', justifyContent: 'space-between' }}>
        <Stack sx={{ pt: 1 }} spacing={2}>
          <Chat turns={c.turns} />
        </Stack>

        <Box sx={{ pb: 2 }}>
          <Stack
            sx={{
              alignItems: 'center',
              px: 2,
              py: 1.5,
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'custom.coral.600',
              borderRadius: '24px',
            }}
            direction='row'
            spacing={1.5}
          >
            <Typography sx={{ flex: 1, minWidth: 0 }} variant='body2'>
              {c.typed}
              {/* The caret: the whole point of the frame is that they are
                  part-way through typing, not looking at an empty field. */}
              <Box
                component='span'
                sx={{
                  display: 'inline-block',
                  width: '2px',
                  height: '1em',
                  bgcolor: 'custom.coral.600',
                  verticalAlign: 'text-bottom',
                }}
              />
            </Typography>
          </Stack>
        </Box>
      </Stack>

      <MobileKeyboard visible />
    </Screen>
  );
};
F5Typing.displayName = 'F5Typing';

// F7 — the claim starting. Compressed deliberately: section C has the whole
// thing. What matters here is that it opens by saying what it already knows,
// so starting a claim doesn't feel like starting again.
export const F7AaStart = () => {
  const c = FLOW.aaStart;
  return (
    <Screen footer={<TalkInput typePrompt={c.typePrompt} state='listening' />}>
      <Stack sx={{ pt: 1 }} spacing={2}>
        <Chat turns={c.turns} />
      </Stack>
    </Screen>
  );
};
F7AaStart.displayName = 'F7AaStart';

// F8 — skipping. The same defer pattern as C2, kept in the flow so the story
// includes the very common case of not having the answer to hand.
export const F8Skip = () => {
  const c = FLOW.skip;
  return (
    <Screen footer={<ChatInput placeholder={c.typePrompt} />}>
      <Stack sx={{ pt: 1 }} spacing={2}>
        <Chat turns={c.turns} />

        <Stack spacing={1}>
          {c.options.map((o) => (
            <Paper key={o.label} variant='outlined'>
              <Stack
                sx={{ alignItems: 'center', p: 1.5 }}
                direction='row'
                spacing={1.5}
              >
                <Box sx={{ color: 'custom.warmOrange.500', display: 'flex' }}>
                  <Icon icon={o.icon as never} fontSize={16} />
                </Box>
                <Typography sx={{ flex: 1, minWidth: 0 }} variant='body2'>
                  {o.label}
                </Typography>
              </Stack>
            </Paper>
          ))}
        </Stack>
      </Stack>
    </Screen>
  );
};
F8Skip.displayName = 'F8Skip';

// F10 — review, with the parked items answerable in place. They sit in their
// own block above the confirmed sections and one is shown mid-answer, because
// the thing being tested is whether finishing can happen here rather than
// sending someone off to find what they skipped.
export const F10Review = () => {
  const c = FLOW.review;
  return (
    <Screen
      title={c.title}
      footer={
        <Button ariaLabel={c.cta} isFullWidth>
          {c.cta}
        </Button>
      }
    >
      <Stack spacing={3}>
        <Typography variant='body2' color='text.secondary'>
          {c.lead}
        </Typography>

        {c.groups.map((group) => (
          <Stack key={group.title} spacing={1}>
            <Typography variant='overline' color='text.secondary'>
              {group.title}
            </Typography>

            {/* One card per group with hairline-separated rows, following the
                financial assessment's review: the question sits above its
                answer and the whole row is the edit target. */}
            <MuiCard sx={{ p: 0, overflow: 'hidden' }}>
              {group.rows.map((row, i) => {
                const answering = row.state === 'answering';
                const open = row.state === 'open';
                return (
                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      borderTop: i === 0 ? 0 : 1,
                      borderColor: 'custom.neutral.200',
                      bgcolor: answering ? 'custom.coral.50' : undefined,
                    }}
                    key={row.question}
                  >
                    <Stack
                      sx={{
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                      direction='row'
                      spacing={2}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant='body2' color='text.secondary'>
                          {row.question}
                        </Typography>
                        <Typography
                          variant='h6'
                          color={
                            open ? 'custom.warmOrange.500' : 'text.primary'
                          }
                        >
                          {row.value || 'Not answered'}
                          {answering && (
                            <Box
                              component='span'
                              sx={{
                                display: 'inline-block',
                                width: '2px',
                                height: '1em',
                                ml: '1px',
                                bgcolor: 'custom.coral.600',
                                verticalAlign: 'text-bottom',
                              }}
                            />
                          )}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          color: answering
                            ? 'custom.coral.600'
                            : 'custom.neutral.400',
                          display: 'flex',
                          flexShrink: 0,
                        }}
                      >
                        <Icon icon={'pen' as never} fontSize={13} />
                      </Box>
                    </Stack>
                  </Box>
                );
              })}
            </MuiCard>
          </Stack>
        ))}
      </Stack>
    </Screen>
  );
};
F10Review.displayName = 'F10Review';

// F11 — submitted, with the call offered rather than required. The claim is
// already in; the CAM is for what comes next, which keeps the human as an
// upgrade instead of a step you have to clear.
export const F11Submitted = () => {
  const c = FLOW.submitted;
  return (
    <Screen
      footer={
        <Stack spacing={2}>
          <Button ariaLabel={c.cta} isFullWidth>
            {c.cta}
          </Button>
          <Button ariaLabel={c.secondary} variant='link' isFullWidth>
            {c.secondary}
          </Button>
        </Stack>
      }
    >
      <Stack sx={{ pt: 5 }} spacing={3}>
        <Box sx={{ color: 'custom.limeGreen.800', display: 'flex' }}>
          <Icon icon={'check-circle' as never} fontSize={44} />
        </Box>

        <Stack spacing={1.5}>
          <Typography variant='h2'>{c.headline}</Typography>
          <Typography variant='body1' color='text.secondary'>
            {c.body}
          </Typography>
        </Stack>

        <Divider />

        <Card
          title={c.callTitle}
          variant='compact'
          subtitle={c.callDetail}
          onClick={() => {}}
        />
      </Stack>
    </Screen>
  );
};
F11Submitted.displayName = 'F11Submitted';
